<script setup lang="ts">
import { ExternalLink, Info, Loader2 } from 'lucide-vue-next';
import { computed, onMounted, ref, watch } from 'vue';

import {
    CAPABILITY_DOT_CLASSES,
    CAPABILITY_MANUAL_STEPS,
    CAPABILITY_STATE_LABELS,
    CapabilityConfig,
    GRANT_LABEL,
    UPDATE_LABEL
} from '@/components/settingsMenu/sections/microsoft-capabilities-ui/microsoftCapabilityConfig';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/toast/use-toast';
import {
    type CapabilityDefinition,
    capabilityState,
    clearConsentAnswer,
    ConsentStatus,
    entraPermissionsUrl,
    listMicrosoftCapabilities,
    MicrosoftCapability,
    openAdminConsent,
    pendingConsentAnswer
} from '@/services/microsoftCapabilities';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';

const tenantStore = useTenantStore();
const authStore = useAuthStore();
const toast = useToast();

const definitions = ref<CapabilityDefinition[]>([]);
const isLoading = ref<boolean>(true);
// The consent path navigates away, so a second click would race the redirect.
const pending = ref<MicrosoftCapability | null>(null);

/** Read off the token the tools are wired from, so the screen and the agent cannot disagree. */
const capabilities = computed(() =>
    definitions.value.map((definition) => ({
        ...capabilityState(definition, authStore.microsoftScopes),
        manualStep: CAPABILITY_MANUAL_STEPS[definition.capability]
    }))
);

const permissionsUrl = computed(() =>
    authStore.microsoftClientId
        ? entraPermissionsUrl(authStore.microsoftClientId)
        : undefined
);

const failed = (title: string, cause: unknown) => {
    toast.toast({
        title,
        description: 'Please try again.',
        variant: 'destructive'
    });
    console.error(cause);
};

async function loadDefinitions() {
    const tenantId = tenantStore.tenantId;
    if (!tenantId) return;

    try {
        const served = await listMicrosoftCapabilities(tenantId);
        // A tenant switch during the fetch already started a fresh load.
        if (tenantId !== tenantStore.tenantId) return;
        definitions.value = served;
    } catch (error) {
        if (tenantId !== tenantStore.tenantId) return;
        failed('Failed to read the Microsoft capabilities', error);
    }
}

// `refresh` runs from both `onMounted` and the credentials watch, and the answer outlives the
// await below: without this, two overlapping calls would each announce the same grant.
let handlingConsentReturn = false;

async function handleConsentReturn() {
    const consent = pendingConsentAnswer();
    if (!consent || handlingConsentReturn) return;
    handlingConsentReturn = true;
    try {
        await actOnConsent(consent);
    } finally {
        handlingConsentReturn = false;
    }
}

async function actOnConsent(
    consent: NonNullable<ReturnType<typeof pendingConsentAnswer>>
) {
    if ('error' in consent) {
        clearConsentAnswer();
        toast.toast({
            title: 'Microsoft did not grant the permission',
            description: consent.error,
            variant: 'destructive'
        });
        return;
    }

    // Left pending until the token has actually been re-read past its cache: the account may
    // still be loading, and the watch below runs this again when it arrives. Announcing the
    // grant early would put a success message over an Enable button.
    if (!(await authStore.refreshMicrosoftScopes())) return;

    clearConsentAnswer();
    // The manual step is announced here too: the admin who granted may never scroll back to the
    // row, and it is the moment they still think the job is done.
    const manualStep = CAPABILITY_MANUAL_STEPS[consent.granted];
    const members =
        'Members get it at their next sign-in, when their session picks up the new permission.';
    toast.toast({
        title: `${CapabilityConfig[consent.granted].name} enabled`,
        description: manualStep ? `${members} ${manualStep.text}` : members
    });
}

const grant = (capability: ReturnType<typeof capabilityState>) => {
    // The parent does not render this section without them; this is what narrows the types.
    if (
        pending.value ||
        !authStore.microsoftClientId ||
        !authStore.azureTenantId
    ) {
        return;
    }

    pending.value = capability.capability;
    openAdminConsent(
        capability.capability,
        capability.missing,
        authStore.microsoftClientId,
        authStore.azureTenantId
    );
};

// Loading covers the whole sequence, not just the definitions: the rows are read off the token,
// so until it lands every capability would render as "not granted", offering a second consent
// over the one still being handled.
// `onMounted` and the watch below both start one, so runs overlap. Only the newest may uncover
// the rows: an earlier one finishing first would show them against a token still on its way.
let latestRefresh = 0;

async function refresh() {
    const run = ++latestRefresh;
    isLoading.value = true;

    try {
        await loadDefinitions();
        // Nothing else on this screen acquires a token, and without one there are no scopes to
        // read. Cache-served, so it is the same token the tools are wired from.
        await authStore.aquireMicrosoftToken();
        await handleConsentReturn();
    } finally {
        if (run === latestRefresh) {
            isLoading.value = false;
        }
    }
}

onMounted(refresh);

// The account arrives on its own schedule, and the state cannot be read without it.
watch(
    [() => tenantStore.tenantId, () => authStore.isMicrosoftCredentialsLoaded],
    refresh
);
</script>

<template>
    <div class="microsoft-capabilities space-y-3">
        <div class="space-y-2">
            <h4 class="text-[0.94rem] font-semibold">
                Microsoft 365 connectors
            </h4>
            <p class="text-sm text-muted-foreground">
                Granted once for the whole organisation, on Microsoft’s own
                consent screen, by someone who administers your Microsoft Entra
                tenant. Members then reach only their own data, with their own
                account.
            </p>
        </div>

        <div v-if="isLoading" class="text-sm text-muted-foreground">
            <Loader2 class="h-4 w-4 animate-spin" />
        </div>

        <template v-else>
            <template
                v-for="capability in capabilities"
                :key="capability.capability"
            >
                <Separator />

                <div class="flex items-center gap-4 py-1">
                    <img
                        :src="CapabilityConfig[capability.capability].icon"
                        :alt="CapabilityConfig[capability.capability].name"
                        class="h-8 w-8 shrink-0 rounded-md object-contain"
                    />
                    <!-- `min-w-0` is what lets the description truncate rather than push the
                         state and the buttons off the row. -->
                    <div class="min-w-0 flex-1">
                        <p class="truncate text-sm font-semibold">
                            {{ CapabilityConfig[capability.capability].name }}
                        </p>
                        <p class="truncate text-xs text-muted-foreground">
                            {{
                                CapabilityConfig[capability.capability]
                                    .description
                            }}
                        </p>
                    </div>

                    <span
                        class="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground"
                    >
                        <span
                            class="h-1.5 w-1.5 rounded-full"
                            :class="CAPABILITY_DOT_CLASSES[capability.status]"
                        />
                        {{ CAPABILITY_STATE_LABELS[capability.status] }}
                    </span>

                    <div class="flex shrink-0 items-center">
                        <Button
                            v-if="
                                capability.status === ConsentStatus.NOT_GRANTED
                            "
                            size="sm"
                            :disabled="pending !== null"
                            @click="grant(capability)"
                        >
                            <Loader2
                                v-if="pending === capability.capability"
                                class="mr-2 h-4 w-4 animate-spin"
                            />
                            {{ GRANT_LABEL }}
                        </Button>
                        <!-- Names the destination: the click only opens the portal page, which
                             is where the admin does the revoking -- nothing here can. -->
                        <Button
                            v-else-if="permissionsUrl"
                            as="a"
                            variant="outline"
                            size="sm"
                            :href="permissionsUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Revoke in Entra
                            <ExternalLink class="ml-2 h-3 w-3" />
                        </Button>
                    </div>
                </div>

                <!-- Below the row rather than in it: the sentence does not fit beside the
                     state, and the row stays one line for every other status. -->
                <div
                    v-if="capability.status === ConsentStatus.INCOMPLETE"
                    class="flex items-center justify-between gap-4 rounded-md bg-orange25/50 px-3 py-2 dark:bg-orangeContrast25/40"
                >
                    <p class="flex items-center gap-2 text-xs text-foreground">
                        <span
                            class="h-1.5 w-1.5 shrink-0 rounded-full bg-orangeContrast25"
                        />
                        More permissions needed for new capabilities. Existing
                        access still works.
                    </p>
                    <Button
                        variant="link"
                        size="xs"
                        class="shrink-0 text-xs underline font-semibold text-foreground"
                        :disabled="pending !== null"
                        @click="grant(capability)"
                    >
                        <Loader2
                            v-if="pending === capability.capability"
                            class="mr-2 h-4 w-4 animate-spin"
                        />
                        {{ UPDATE_LABEL }}
                    </Button>
                </div>

                <!-- Last, so the orange band above stays next to its own row. Permanent and
                     neutral: the setting is off in every tenant by default, which makes this a
                     step of the connection rather than a fault. -->
                <div
                    v-if="capability.manualStep"
                    class="flex items-center justify-between gap-4 rounded-md bg-muted/30 px-3 py-2"
                >
                    <p
                        class="flex items-start gap-2 text-xs text-muted-foreground"
                    >
                        <Info class="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {{ capability.manualStep.text }}
                    </p>
                    <a
                        :href="capability.manualStep.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex shrink-0 items-center text-xs underline underline-offset-4"
                    >
                        {{ capability.manualStep.linkLabel }}
                        <ExternalLink class="ml-1 h-3 w-3" />
                    </a>
                </div>
            </template>
        </template>
    </div>
</template>
